import React from 'react';
import PaperclipIcon from './ui/PaperclipIcon';
import ProgressBar from './ProgressBar';

/**
 * ONE attachment row while its file is UPLOADING — or after it FAILED.
 * Added 2026-09-14. Owner: *"can we add an uploading green bar between 'Driver
 * USB-INFRARED (1).ZIP (7.1 MB) Download Remove' with a % — the bar should go
 * from the name to the download button"*, then *"use the same green bar style
 * as the item delivery"*.
 *
 * 🔑 **THE BAR IS `components/ProgressBar.js`, SIZED EXACTLY AS THE ORDER TABLE'S
 * DELIVERY BAR** (`OrderTableView.js`'s PO cell: `className="flex-1 min-w-0"`,
 * its count beside it `shrink-0 text-xs tabular-nums text-gray-500
 * dark:text-gray-400`). No second bar, no new colour, height or animation.
 * The percentage has a FIXED width so the bar does not jitter as it counts.
 *
 * 🚨 **NO Download AND NO Remove WHILE UPLOADING.** The file has no stored row
 * yet, so there is nothing to download and nothing the DELETE route could
 * remove. When it lands the caller refreshes and this row is replaced by the
 * ordinary one; when it fails the bar goes, the server's MESSAGE takes its
 * place, and ✕ dismisses the row — re-picking the file is the retry.
 *
 * ⚠️ jsdom has no layout: that the bar spans "from the name to the download
 * button" is a BROWSER check. The tests pin the STRUCTURE (name, then the bar,
 * then the percentage) and the accessible progressbar.
 *
 * Props: `file` (a File), `fraction` 0…1, `error` (string | null), `onDismiss`,
 * `className` (the caller's row chrome), `testId`.
 *
 * 🔀 **TWO OPTIONAL PROPS, 2026-09-15 (R13), both default null → byte-identical
 * for every caller that passes neither.** A PENDING upload
 * (`hooks/usePendingOrderUploads.js`) has a server name from the moment it
 * starts, so it CAN be removed mid-upload and retried after a refusal:
 * `onCancel` adds a ✕ beside the percentage (abort + discard), `onRetry` adds
 * a Retry beside a failed row's ✕ (the same File, re-sent).
 */
function UploadingFileRow({ file, fraction = 0, error = null, onDismiss = null, onRetry = null, onCancel = null, className = '', testId = 'uploading-file' }) {
  const name = (file && file.name) || 'file';
  const pct = Math.round(Math.min(1, Math.max(0, Number(fraction) || 0)) * 100);
  return (
    <div
      className={`flex items-center gap-2 ${className}`}
      data-testid={testId}
      data-upload-state={error ? 'failed' : 'uploading'}
    >
      <div className="flex items-center gap-1.5 min-w-0 max-w-[50%]">
        <PaperclipIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
        <span className="text-gray-700 dark:text-gray-300 truncate" data-testid={`${testId}-name`}>{name}</span>
      </div>
      {error ? (
        <>
          <span
            className="flex-1 min-w-0 truncate text-red-600 dark:text-red-400"
            role="alert"
            title={error}
            data-testid={`${testId}-error`}
          >
            {error}
          </span>
          {typeof onRetry === 'function' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRetry(); }}
              className="shrink-0 font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              aria-label={`Retry uploading ${name}`}
              data-testid={`${testId}-retry`}
            >
              Retry
            </button>
          )}
          {typeof onDismiss === 'function' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDismiss(); }}
              className="shrink-0 font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label={`Dismiss ${name}`}
              data-testid={`${testId}-dismiss`}
            >
              ✕
            </button>
          )}
        </>
      ) : (
        <span className="flex-1 min-w-0 flex items-center gap-1.5" data-testid={`${testId}-progress`}>
          <ProgressBar done={pct} total={100} className="flex-1 min-w-0" ariaLabel={`Uploading ${name}`} />
          <span className="shrink-0 w-9 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400" data-testid={`${testId}-percent`}>
            {pct}%
          </span>
          {typeof onCancel === 'function' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onCancel(); }}
              className="shrink-0 font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label={`Cancel uploading ${name}`}
              data-testid={`${testId}-cancel`}
            >
              ✕
            </button>
          )}
        </span>
      )}
    </div>
  );
}

export default UploadingFileRow;
